import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../api";

/*
========================================================
PLATFORM TENANT INTELLIGENCE
--------------------------------------------------------
Purpose:
- Dedicated tenant intelligence page for platform admins
- Shows live SaaS tenant intelligence
- Shows operational workflow activity across tenants
- Shows tenant health, billing posture, revenue telemetry, and workflow activity
========================================================
*/

export default function PlatformTenantIntelligence() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIntelligence();
  }, []);

  async function loadIntelligence() {
    setLoading(true);
    setErr("");

    try {
      const result = await apiRequest("/platform/intelligence");
      setData(result);
    } catch (error) {
      setErr(error.message || "Failed to load platform intelligence.");
    } finally {
      setLoading(false);
    }
  }

  const actionBtn = {
    padding: "12px 18px",
    borderRadius: 8,
    background: "#e0b84f",
    color: "#0b1b2b",
    fontWeight: 700,
    cursor: "pointer",
    border: "none",
    minWidth: 220,
    textAlign: "center",
  };

  const sectionCard = {
    background: "#13293d",
    borderRadius: 12,
    padding: 20,
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: 20,
  };

  const buttonGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    marginTop: 16,
  };

  const cardGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 14,
    marginTop: 16,
  };

  const metricCard = {
    background: "#0b1b2b",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: 16,
  };

  const labelStyle = {
    color: "#9fb0c3",
    fontSize: 12,
    marginBottom: 8,
  };

  const valueStyle = {
    color: "#ffffff",
    fontSize: 26,
    fontWeight: 800,
  };

  const tableWrap = {
    overflowX: "auto",
    marginTop: 14,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const thStyle = {
    textAlign: "left",
    padding: "12px 10px",
    color: "#9fb0c3",
    fontSize: 12,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "12px 10px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    verticalAlign: "top",
    fontSize: 13,
  };

  const tenants = data?.tenants || [];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: "0 0 8px" }}>Tenant Intelligence</h1>
        <div style={{ color: "#9fb0c3", fontSize: 16 }}>
          Dedicated view of tenant health, revenue telemetry, billing posture, and operational maturity.
        </div>
      </div>

      {err && (
        <div
          style={{
            ...sectionCard,
            border: "1px solid rgba(248,113,113,0.35)",
            background: "rgba(127,29,29,0.25)",
            color: "#fecaca",
          }}
        >
          {err}
        </div>
      )}

      {loading && (
        <div style={sectionCard}>Loading platform intelligence...</div>
      )}

      {!loading && data && (
        <>
          {/* ============================== */}
          {/* TENANT INTELLIGENCE CARDS */}
          {/* ============================== */}
          <div style={sectionCard}>
            <h2 style={{ marginTop: 0 }}>Tenant Intelligence</h2>

            <div style={{ color: "#9fb0c3", marginBottom: 18 }}>
              Operational health, billing posture, workflow activity, and revenue telemetry for each tenant workspace.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 18,
              }}
            >
              {tenants.map((t) => (
                <div
                  key={t.id}
                  style={{
                    background: "#0b1b2b",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 14,
                    padding: 18,
                  }}
                >
                  {/* ================================= */}
                  {/* TENANT HEADER */}
                  {/* ================================= */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 800,
                          color: "#ffffff",
                        }}
                      >
                        {t.name}
                      </div>

                      <div
                        style={{
                          color: "#9fb0c3",
                          fontSize: 12,
                          marginTop: 4,
                        }}
                      >
                        Workspace: {t.subdomain} · Tenant ID {t.id}
                      </div>
                    </div>

                    <button
                      style={{
                        ...actionBtn,
                        minWidth: 100,
                        padding: "8px 10px",
                        fontSize: 12,
                      }}
                      onClick={() => navigate(`/platform/tenants/${t.id}`)}
                    >
                      Open Cockpit
                    </button>
                  </div>

                  {/* ================================= */}
                  {/* HEALTH SIGNALS */}
                  {/* ================================= */}
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      flexWrap: "wrap",
                      marginBottom: 14,
                    }}
                  >
                    {(t.metrics?.health_signals || []).map((signal, idx) => (
                      <span
                        key={idx}
                        title={signal.meaning}
                        style={{
                          padding: "5px 9px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: signalColor(signal.color),
                          color: "#ffffff",
                        }}
                      >
                        {signal.label}
                      </span>
                    ))}
                  </div>

                  {/* ================================= */}
                  {/* BILLING STATE */}
                  {/* ================================= */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <MiniMetric
                      label="Tenant Type"
                      value={title(t.tenant_type || "standard")}
                      help="Platform classification: internal, beta, standard, trial, paid, or suspended."
                    />

                    <MiniMetric
                      label="Plan"
                      value={`${title(t.subscription_plan || "—")} Plan`}
                      help="Current operational subscription level."
                    />

                    <MiniMetric
                      label="Billing"
                      value={`${title(t.billing_mode || "—")} / ${title(t.subscription_status || "—")}`}
                      help="Current tenant billing posture and subscription state."
                    />
                  </div>

                  {/* ================================= */}
                  {/* WORKFLOW METRICS */}
                  {/* ================================= */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <MiniMetric
                      label="Clients"
                      value={t.metrics?.clients ?? 0}
                      help="Total clients associated with this tenant."
                    />

                    <MiniMetric
                      label="Jobs"
                      value={t.metrics?.jobs ?? 0}
                      help="Operational jobs/workflow records."
                    />

                    <MiniMetric
                      label="Invoices"
                      value={t.metrics?.invoices ?? 0}
                      help="Revenue events created in JobTelesis."
                    />
                  </div>

                  {/* ================================= */}
                  {/* REVENUE + CONVERSION */}
                  {/* ================================= */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <MiniMetric
                      label="Revenue Processed"
                      value={`$${Number(t.metrics?.invoice_revenue || 0).toLocaleString()}`}
                      help="Total invoice value processed through this workspace."
                    />

                    <MiniMetric
                      label="Payments Collected"
                      value={`$${Number(t.metrics?.payments_collected || 0).toLocaleString()}`}
                      help="Total payments recorded as collected."
                    />

                    <MiniMetric
                      label="Conversion"
                      value={`${t.metrics?.estimate_to_invoice_conversion_rate ?? 0}%`}
                      help="How many estimates became invoices."
                    />

                    <MiniMetric
                      label="Stripe"
                      value={t.stripe_connected ? "Connected" : "Not Connected"}
                      help="Indicates whether Stripe customer infrastructure is connected."
                    />
                  </div>

                  {/* ================================= */}
                  {/* LAST ACTIVITY */}
                  {/* ================================= */}
                  <div
                    style={{
                      borderTop: "1px solid rgba(255,255,255,0.06)",
                      paddingTop: 12,
                    }}
                  >
                    <div
                      style={{
                        color: "#9fb0c3",
                        fontSize: 11,
                        marginBottom: 4,
                      }}
                    >
                      Last Activity
                    </div>

                    <div style={{ fontSize: 13 }}>
                      {t.metrics?.last_activity
                        ? t.metrics.last_activity.document_number
                          ? `${t.metrics.last_activity.activity_type} · ${t.metrics.last_activity.document_type} ${t.metrics.last_activity.document_number}`
                          : `${t.metrics.last_activity.activity_type} · ${t.metrics.last_activity.document_type}`
                        : "No recent workflow activity"}
                    </div>

                    <div
                      style={{
                        color: "#9fb0c3",
                        fontSize: 11,
                        marginTop: 6,
                      }}
                    >
                      Most recent operational workflow event detected for this tenant.
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  function MetricCard({ label, value }) {
    return (
      <div style={metricCard}>
        <div style={labelStyle}>{label}</div>
        <div style={valueStyle}>{value ?? 0}</div>
      </div>
    );
  }
}

function signalColor(color) {
  const colors = {
    green: "#166534",
    blue: "#1d4ed8",
    yellow: "#a16207",
    orange: "#c2410c",
    red: "#991b1b",
  };

  return colors[color] || "#334155";
}

function MiniMetric({ label, value, help }) {
  return (
    <div
      title={help}
      style={{
        background: "#13293d",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 10,
        padding: 12,
      }}
    >
      <div style={{ color: "#9fb0c3", fontSize: 11, marginBottom: 6 }}>
        {label}
      </div>

      <div style={{ fontWeight: 800, fontSize: 16, color: "#ffffff" }}>
        {value}
      </div>
    </div>
  );
}

function title(value) {
  if (!value) return "—";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}
